import {useCallback, useRef} from "react"

import {trackEvent} from "@frontend/utils/trackEvent"
import {
  useBootstrapStarterCaseMutation,
  useCreateCaseMutation,
} from "@frontend/utils/trpc"

interface Args {
  userRcId: string
  pageContext?: Ri2PageContext
  starterPayload?: Ri2StarterBootstrapPayload | null
  onCaseCreated: (
    caseId: string,
    message: string | null,
    starterPayload?: Ri2StarterBootstrapPayload | null,
  ) => void
}

type StartSource = "typed" | "starter" | "api"

const trackWidgetConversationStarted = (
  caseId: string,
  startSource: StartSource,
): void => {
  trackEvent("widget_chat_started", caseId)
  trackEvent(
    startSource === "api"
      ? "widget_chat_started_api"
      : startSource === "starter"
        ? "widget_chat_started_starter"
        : "widget_chat_started_typed",
    caseId,
  )
  trackEvent(
    window.ri2?.displayMode === "slideout"
      ? "widget_chat_started_slideout"
      : "widget_chat_started_overlay",
    caseId,
  )
}

export const useStarterCaseCreation = ({
  userRcId,
  pageContext,
  starterPayload,
  onCaseCreated,
}: Args): {
  isPending: boolean
  startTypedCase: (message: string) => void
  startStarterCase: (
    message: string,
    source?: Exclude<StartSource, "typed">,
  ) => void
} => {
  const pendingMessageRef = useRef("")
  const pendingStartSourceRef = useRef<StartSource>("typed")
  const pendingStarterPayloadRef = useRef<Ri2StarterBootstrapPayload | null>(
    null,
  )
  const pendingUsesBootstrapRef = useRef(false)

  const createCaseMutation = useCreateCaseMutation(({caseId}): void => {
    trackWidgetConversationStarted(caseId, pendingStartSourceRef.current)
    onCaseCreated(
      caseId,
      pendingMessageRef.current,
      pendingStarterPayloadRef.current,
    )
  })
  const bootstrapStarterCaseMutation = useBootstrapStarterCaseMutation(
    ({caseId}): void => {
      trackWidgetConversationStarted(caseId, pendingStartSourceRef.current)
      onCaseCreated(
        caseId,
        pendingUsesBootstrapRef.current ? null : pendingMessageRef.current,
        pendingStarterPayloadRef.current,
      )
    },
  )

  const startCase = useCallback(
    (message: string, source: StartSource): void => {
      const shouldUseBootstrap =
        source !== "typed" && (starterPayload?.compatibleParts?.length ?? 0) > 0

      pendingStartSourceRef.current = source
      pendingMessageRef.current = message
      pendingStarterPayloadRef.current = shouldUseBootstrap
        ? (starterPayload ?? null)
        : null
      pendingUsesBootstrapRef.current = shouldUseBootstrap

      if (shouldUseBootstrap && starterPayload) {
        bootstrapStarterCaseMutation.mutate({
          userRcId,
          pageContext: pageContext ?? undefined,
          starterMessage: message,
          starterPayload,
        })
        return
      }

      createCaseMutation.mutate({
        userRcId,
        pageContext: pageContext ?? undefined,
      })
    },
    [
      bootstrapStarterCaseMutation,
      createCaseMutation,
      pageContext,
      starterPayload,
      userRcId,
    ],
  )

  return {
    isPending:
      createCaseMutation.isPending || bootstrapStarterCaseMutation.isPending,
    startTypedCase: useCallback(
      (message: string) => {
        startCase(message, "typed")
      },
      [startCase],
    ),
    startStarterCase: useCallback(
      (message: string, source: "starter" | "api" = "starter") => {
        startCase(message, source)
      },
      [startCase],
    ),
  }
}
